import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  getValidationScoringConfigDefaults,
  getComparisonScoringConfigDefaults,
  getValidationRunScoringConfigDefaults,
  getComparisonRunScoringConfigDefaults,
  getResearchQueryRunScoringConfigDefaults,
  getRunScoringPipelineWeightConfigDefaults,
} from "../../services/scoringService";
import {
  validationScoringConfigDefaults,
  comparisonScoringConfigDefaults,
  genericRQScoringConfigDefaults,
  validationRunScoringConfigDefaults,
  comparisonRunScoringConfigDefaults,
  researchQueryRunScoringConfigDefaults,
  pipelineWeightConfigDefaults,
} from "../../services/constants";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

// Hardened value renderer — handles primitives, null/undefined, and nested
// objects (pretty-printed JSON) to prevent "[object Object]" in the UI.
const renderValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          fontFamily:
            '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: 12,
          lineHeight: 1.45,
          bgcolor: "#f6f8fa",
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 1,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }
  return String(value);
};

// Normalize API responses that may use snapshot-style keys
// (pipeline_weight_*, default_entity_*) instead of the UI-expected keys.
const normalizePipelineWeight = (res: any) => {
  if (!res || typeof res !== "object") return {};
  return {
    ...res,
    validation_weight: res.validation_weight ?? res.pipeline_weight_validation,
    comparison_weight: res.comparison_weight ?? res.pipeline_weight_comparison,
    research_query_weight:
      res.research_query_weight ?? res.pipeline_weight_research_query,
  };
};

// Aggregation run-scoring endpoints (validation / comparison / research query)
// currently return { entity_factor, entity_severity }. Older snapshot style
// uses { default_entity_factor, default_entity_severity }. Map either form
// onto the UI's { factor, severity } shape.
const normalizeRunScoring = (res: any) => {
  if (!res || typeof res !== "object") return {};
  return {
    ...res,
    severity:
      res.severity ?? res.entity_severity ?? res.default_entity_severity,
    factor: res.factor ?? res.entity_factor ?? res.default_entity_factor,
  };
};

// Returns true if the merged object has at least one meaningful value
const hasAnyValues = (obj: Record<string, unknown>): boolean =>
  Object.values(obj).some((v) => v !== null && v !== undefined && v !== "");

/**
 * AggregationFormulaDetails — shared documentation accordion used in every
 * Aggregation Score Config tab. Mirrors the inline "Scoring Formula Details"
 * style already established in the Run Score Config tabs of this file, and
 * the `final_score` formula tooltip in the AggregatedScoreDashboard hero.
 *
 * `entityLabel` lets the per-pipeline tabs frame Level 1 in their own terms
 * ("Resources" / "Rules" / "Queries"); the Pipeline Weight tab uses the
 * generic default since it cuts across all three pipelines.
 */
const AggregationFormulaDetails: React.FC<{ entityLabel?: string }> = ({
  entityLabel = "entities",
}) => (
  <>
    <Divider sx={{ my: 3 }} />
    <Accordion defaultExpanded={false} variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: "var(--orange)" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Scoring Formula Details
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            fontFamily: "monospace",
            fontSize: "0.82rem",
            lineHeight: 1.8,
            bgcolor: "grey.50",
            p: 2,
            borderRadius: 1,
            "& strong": { color: "var(--orange)" },
          }}
        >
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Level 1 — Within-pipeline aggregation
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontFamily: "inherit" }}
          >
            Each pipeline's score is the factor-weighted average of its{" "}
            {entityLabel}'s scores.
          </Typography>
          <code>
            <strong>
              pipeline_score = Σ(entity_factor × entity_score) /
              Σ(entity_factor)
            </strong>
          </code>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            "Entities" refers to <strong>Rules</strong> (Comparison),{" "}
            <strong>Resources</strong> (Validation), or <strong>Queries</strong>{" "}
            (Research Query). The factor reflects each entity's relative
            importance within its pipeline.
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Level 2 — Cross-pipeline aggregation
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontFamily: "inherit" }}
          >
            The final run score is the weight-weighted average of the active
            pipeline scores.
          </Typography>
          <code>
            <strong>
              final_score = Σ(pipeline_weight × pipeline_score) /
              Σ(pipeline_weight)
            </strong>
          </code>
          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Pipeline Inclusion Rule
          </Typography>
          <Typography
            variant="body2"
            component="div"
            color="text.secondary"
            sx={{ lineHeight: 1.9, fontFamily: "inherit" }}
          >
            The Level 2 calculation only includes pipelines that have{" "}
            <strong>at least one execution</strong> for the selected run/score
            date. If a pipeline has no data, its weight is excluded from{" "}
            <strong>both the numerator and the denominator</strong> — it
            contributes nothing and does not penalize the final score.
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  </>
);

// URL parameter keys used to persist tab state, mirroring ScoringConfig so
// deep links and the browser back button behave consistently across both
// scoring pages.
const URL_PARAM_TAB = "tab";
const URL_PARAM_RUN_SUB = "runSub";
const URL_PARAM_AGG_SUB = "aggSub";

const parseTabParam = (v: string | null): number => {
  if (v === null) return 0;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

// ── Shared tab style helpers ──────────────────────────────────────────────────

const parentTabSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: "0.95rem",
  color: "text.secondary",
  "&.Mui-selected": { color: "var(--orange)", fontWeight: 700 },
};

const subTabSx = {
  textTransform: "none" as const,
  fontWeight: 400,
  fontSize: "0.875rem",
  color: "text.secondary",
  "&.Mui-selected": { color: "var(--auto-sky-blue)", fontWeight: 600 },
};

const tabIndicatorSx = {
  "& .MuiTabs-indicator": { backgroundColor: "var(--auto-sky-blue)", height: 3 },
};

const ScoringDefaults = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // 0 = Run Score Config | 1 = Aggregation Score Config
  const [parentTab, setParentTab] = useState(() =>
    parseTabParam(searchParams.get(URL_PARAM_TAB)),
  );
  // Sub-tab indices are independent per parent so switching parents restores position
  const [runSubTab, setRunSubTab] = useState(() =>
    parseTabParam(searchParams.get(URL_PARAM_RUN_SUB)),
  );
  const [aggSubTab, setAggSubTab] = useState(() =>
    parseTabParam(searchParams.get(URL_PARAM_AGG_SUB)),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [vd, setVd] = useState(validationScoringConfigDefaults);
  const [cd, setCd] = useState(comparisonScoringConfigDefaults);
  const gd = genericRQScoringConfigDefaults;
  const [vrd, setVrd] = useState(validationRunScoringConfigDefaults);
  const [crd, setCrd] = useState(comparisonRunScoringConfigDefaults);
  const [rqrd, setRqrd] = useState(researchQueryRunScoringConfigDefaults);
  const [pwd, setPwd] = useState(pipelineWeightConfigDefaults);

  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const [
          valDefaults,
          compDefaults,
          valRunDefaults,
          compRunDefaults,
          rqRunDefaults,
          pipelineWeightDefaults,
        ] = await Promise.all([
          getValidationScoringConfigDefaults(),
          getComparisonScoringConfigDefaults(),
          getValidationRunScoringConfigDefaults(),
          getComparisonRunScoringConfigDefaults(),
          getResearchQueryRunScoringConfigDefaults(),
          getRunScoringPipelineWeightConfigDefaults(),
        ]);
        // Merge API response onto hardcoded fallbacks so missing or renamed
        // keys don't blank out the UI; normalize snapshot-style key names first.
        if (valDefaults && typeof valDefaults === "object") {
          setVd({ ...validationScoringConfigDefaults, ...valDefaults });
        }
        if (compDefaults && typeof compDefaults === "object") {
          setCd({ ...comparisonScoringConfigDefaults, ...compDefaults });
        }
        if (valRunDefaults && typeof valRunDefaults === "object") {
          setVrd({
            ...validationRunScoringConfigDefaults,
            ...normalizeRunScoring(valRunDefaults),
          });
        }
        if (compRunDefaults && typeof compRunDefaults === "object") {
          setCrd({
            ...comparisonRunScoringConfigDefaults,
            ...normalizeRunScoring(compRunDefaults),
          });
        }
        if (rqRunDefaults && typeof rqRunDefaults === "object") {
          setRqrd({
            ...researchQueryRunScoringConfigDefaults,
            ...normalizeRunScoring(rqRunDefaults),
          });
        }
        if (
          pipelineWeightDefaults &&
          typeof pipelineWeightDefaults === "object"
        ) {
          setPwd({
            ...pipelineWeightConfigDefaults,
            ...normalizePipelineWeight(pipelineWeightDefaults),
          });
        }
      } catch (err) {
        console.error(
          "Failed to fetch scoring defaults from API, using hardcoded fallbacks",
          err,
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchDefaults();
  }, []);

  // URL → State: keep tab state in sync with browser back/forward navigation.
  // Only updates state when the corresponding param is present in the URL,
  // so the inactive parent's subtab position is preserved across parent‑tab
  // switches that intentionally omit the other subtab key. Mirrors the
  // implementation in ScoringConfig.
  useEffect(() => {
    const tabParam = searchParams.get(URL_PARAM_TAB);
    const runSubParam = searchParams.get(URL_PARAM_RUN_SUB);
    const aggSubParam = searchParams.get(URL_PARAM_AGG_SUB);

    if (tabParam !== null) {
      const v = parseTabParam(tabParam);
      if (v !== parentTab) setParentTab(v);
    }
    if (runSubParam !== null) {
      const v = parseTabParam(runSubParam);
      if (v !== runSubTab) setRunSubTab(v);
    }
    if (aggSubParam !== null) {
      const v = parseTabParam(aggSubParam);
      if (v !== aggSubTab) setAggSubTab(v);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab change handlers push a new history entry so the browser back button
  // returns to the previous tab.
  const handleParentTabChange = (_: React.SyntheticEvent, v: number) => {
    setParentTab(v);
    const next = new URLSearchParams();
    next.set(URL_PARAM_TAB, String(v));
    if (v === 0) next.set(URL_PARAM_RUN_SUB, String(runSubTab));
    else next.set(URL_PARAM_AGG_SUB, String(aggSubTab));
    setSearchParams(next);
  };

  const handleRunSubTabChange = (_: React.SyntheticEvent, v: number) => {
    setRunSubTab(v);
    const next = new URLSearchParams();
    next.set(URL_PARAM_TAB, "0");
    next.set(URL_PARAM_RUN_SUB, String(v));
    setSearchParams(next);
  };

  const handleAggSubTabChange = (_: React.SyntheticEvent, v: number) => {
    setAggSubTab(v);
    const next = new URLSearchParams();
    next.set(URL_PARAM_TAB, "1");
    next.set(URL_PARAM_AGG_SUB, String(v));
    setSearchParams(next);
  };

  return (
    <Box sx={{ p: 2 }}>
      {isLoading && <Loader isLoading={isLoading} />}
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Scoring Defaults
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        System default scoring parameters. These values are used when no
        resource-specific or rule-specific override is configured. To override
        for a specific resource or rule, use the "Scoring Config" tab on the
        resource or rule edit page. Defaults are managed via environment
        variables on the backend.
      </Typography>

      <Paper variant="outlined">
        {/* ── Parent tabs ──────────────────────────────────────────────────── */}
        <Tabs
          value={parentTab}
          onChange={handleParentTabChange}
          sx={{
            ...tabIndicatorSx,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Tab label="Run Score Config" sx={parentTabSx} />
          <Tab label="Aggregation Score Config" sx={parentTabSx} />
        </Tabs>

        {/* ── Run Score Config ─────────────────────────────────────────────── */}
        {parentTab === 0 && (
          <>
            <Tabs
              value={runSubTab}
              onChange={handleRunSubTabChange}
              sx={{
                ...tabIndicatorSx,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Tab label="Validation Scoring Defaults" sx={subTabSx} />
              <Tab label="Comparison Scoring Defaults" sx={subTabSx} />
              <Tab label="Generic RQ Scoring Defaults" sx={subTabSx} />
            </Tabs>

            <TabPanel value={runSubTab} index={0}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Default Validation Scoring Parameters
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  These defaults apply to any resource that does not have a
                  resource-specific scoring config.
                </Typography>
                {hasAnyValues(vd) ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Parameter</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Default Value</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Impact of Record Level</TableCell>
                          <TableCell align="right">
                            {renderValue(vd.impact_of_record_level)}
                          </TableCell>
                          <TableCell>
                            Weight applied to record-level error impacts (0-1)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Impact of Issue Level</TableCell>
                          <TableCell align="right">
                            {renderValue(vd.impact_of_issue_level)}
                          </TableCell>
                          <TableCell>
                            Weight applied to issue-level error impacts (0-1)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor Low</TableCell>
                          <TableCell align="right">
                            {renderValue(vd.factor_low)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for low-severity errors
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor Medium</TableCell>
                          <TableCell align="right">
                            {renderValue(vd.factor_medium)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for medium-severity errors
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor High</TableCell>
                          <TableCell align="right">
                            {renderValue(vd.factor_high)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for high-severity errors
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No default configuration found for this category.
                  </Paper>
                )}
                <Divider sx={{ my: 3 }} />
                <Accordion defaultExpanded={false} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Scoring Formula Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.82rem",
                        lineHeight: 1.8,
                        bgcolor: "grey.50",
                        p: 2,
                        borderRadius: 1,
                        "& strong": { color: "var(--orange)" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Step 1 — Per-record impact
                      </Typography>
                      <code>each_records_impact = 1 / total_records</code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 2 — Record-level penalty (per severity)
                      </Typography>
                      <code>
                        record_impact(s) = each_records_impact ×
                        records_with_issues(s) × factor(s) ×
                        impact_of_record_level
                      </code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 3 — Issue-level penalty (per severity)
                      </Typography>
                      <code>
                        issue_impact(s) = each_records_impact × total_issues(s)
                        × factor(s) × impact_of_issue_level
                      </code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 4 — Final score
                      </Typography>
                      <code>
                        <strong>
                          score = max(0, 1 − Σ record_impact(s) − Σ
                          issue_impact(s))
                        </strong>
                      </code>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        Where <em>s</em> ∈ {"{"} low, medium, high {"}"} and
                        factor(s) is the severity multiplier from the table
                        above. The score is clamped to [0, 1].
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Worked Example
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ lineHeight: 1.9 }}
                      >
                        100 records · records_with_issues: low=5, med=3, high=1
                        · issues: low=5, med=3, high=1
                        <br />
                        <code>each = 1/100 = 0.01</code>
                        <br />
                        <code>
                          record_penalty = 0.01 × (5×0.25 + 3×0.50 + 1×1.00) ×
                          0.5 = 0.01625
                        </code>
                        <br />
                        <code>
                          issue_penalty &nbsp;= 0.01 × (5×0.25 + 3×0.50 +
                          1×1.00) × 0.5 = 0.01625
                        </code>
                        <br />
                        <code>
                          <strong>
                            score = max(0, 1 − 0.01625 − 0.01625) = 0.9675
                          </strong>
                        </code>
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </TabPanel>

            <TabPanel value={runSubTab} index={1}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Default Comparison Scoring Parameters
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  These defaults apply to any rule that does not have a
                  rule-specific scoring config.
                </Typography>
                {hasAnyValues(cd) ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Parameter</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Default Value</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Category Weight: Missing</TableCell>
                          <TableCell align="right">
                            {renderValue(cd.category_weight_missing)}
                          </TableCell>
                          <TableCell>
                            Weight for missing records penalty (sum to 1.0)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Category Weight: Mismatch</TableCell>
                          <TableCell align="right">
                            {renderValue(cd.category_weight_mismatch)}
                          </TableCell>
                          <TableCell>
                            Weight for mismatched records penalty (sum to 1.0)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Category Weight: Validation</TableCell>
                          <TableCell align="right">
                            {renderValue(cd.category_weight_validation)}
                          </TableCell>
                          <TableCell>
                            Weight for validation error penalty (sum to 1.0)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Missing Factor</TableCell>
                          <TableCell align="right">
                            {renderValue(cd.missing_factor)}
                          </TableCell>
                          <TableCell>
                            Amplification factor for missing records
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Mismatch Factor Low</TableCell>
                          <TableCell align="right">
                            {renderValue(cd.mismatch_factor_low)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for low-severity mismatches
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Mismatch Factor Medium</TableCell>
                          <TableCell align="right">
                            {renderValue(cd.mismatch_factor_medium)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for medium-severity mismatches
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Mismatch Factor High</TableCell>
                          <TableCell align="right">
                            {renderValue(cd.mismatch_factor_high)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for high-severity mismatches
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Blend Ratio</TableCell>
                          <TableCell align="right">
                            {renderValue(cd.blend_ratio)}
                          </TableCell>
                          <TableCell>
                            1.0 = count-only scoring (phase 1)
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No default configuration found for this category.
                  </Paper>
                )}
                <Divider sx={{ my: 3 }} />
                <Accordion defaultExpanded={false} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Scoring Formula Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.82rem",
                        lineHeight: 1.8,
                        bgcolor: "grey.50",
                        p: 2,
                        borderRadius: 1,
                        "& strong": { color: "var(--orange)" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Step 1 — Missing penalty
                      </Typography>
                      <code>
                        missing_penalty = (missing_records / total_records) ×
                        missing_factor × category_weight_missing
                      </code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 2 — Mismatch penalty (per severity)
                      </Typography>
                      <code>
                        mismatch_penalty(s) = (mismatched_records(s) /
                        total_records) × mismatch_factor(s) ×
                        category_weight_mismatch
                      </code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 3 — Validation penalty
                      </Typography>
                      <code>
                        validation_penalty = (1 − validation_score) ×
                        category_weight_validation
                      </code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 4 — Final score
                      </Typography>
                      <code>
                        <strong>
                          score = max(0, 1 − missing_penalty − Σ
                          mismatch_penalty(s) − validation_penalty)
                        </strong>
                      </code>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        Where <em>s</em> ∈ {"{"} low, medium, high {"}"}.
                        Category weights should sum to 1.0. The blend_ratio of
                        1.0 means count-only scoring (phase 1 implementation).
                        Zero total records yields a perfect 1.0.
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Worked Example
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ lineHeight: 1.9 }}
                      >
                        100 records · 5 missing · mismatches: low=3, med=2,
                        high=1 · validation scores: [0.90, 0.80]
                        <br />
                        <code>
                          missing &nbsp;&nbsp;= (5/100) × 3.0 × 0.70 = 0.1050
                        </code>
                        <br />
                        <code>
                          mm_low &nbsp;&nbsp;&nbsp;= (3/100) × 0.25 × 0.20 =
                          0.0015
                        </code>
                        <br />
                        <code>
                          mm_med &nbsp;&nbsp;&nbsp;= (2/100) × 0.50 × 0.20 =
                          0.0020
                        </code>
                        <br />
                        <code>
                          mm_high &nbsp;&nbsp;= (1/100) × 1.00 × 0.20 = 0.0020
                        </code>
                        <br />
                        <code>
                          validation = (0.10/2 + 0.20/2) × 0.10 = 0.0150
                        </code>
                        <br />
                        <code>
                          <strong>
                            score = max(0, 1 − 0.105 − 0.0055 − 0.015) = 0.8745
                          </strong>
                        </code>
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </TabPanel>

            <TabPanel value={runSubTab} index={2}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Default Generic RQ Scoring Parameters
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Generic Research Query scoring uses the same formula and field
                  names as validation scoring. When issues are disabled on a
                  query, the issue-level penalty contribution is skipped.
                </Typography>
                {hasAnyValues(gd) ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Parameter</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Default Value</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Impact of Record Level</TableCell>
                          <TableCell align="right">
                            {renderValue(gd.impact_of_record_level)}
                          </TableCell>
                          <TableCell>
                            Weight applied to record-level error impacts (0-1)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Impact of Issue Level</TableCell>
                          <TableCell align="right">
                            {renderValue(gd.impact_of_issue_level)}
                          </TableCell>
                          <TableCell>
                            Weight applied to issue-level error impacts (0-1).
                            Ignored when issues are disabled on the query.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor Low</TableCell>
                          <TableCell align="right">
                            {renderValue(gd.factor_low)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for low-severity errors
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor Medium</TableCell>
                          <TableCell align="right">
                            {renderValue(gd.factor_medium)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for medium-severity errors
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor High</TableCell>
                          <TableCell align="right">
                            {renderValue(gd.factor_high)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier for high-severity errors
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No default configuration found for this category.
                  </Paper>
                )}
                <Divider sx={{ my: 3 }} />
                <Accordion defaultExpanded={false} variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Scoring Formula Details
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.82rem",
                        lineHeight: 1.8,
                        bgcolor: "grey.50",
                        p: 2,
                        borderRadius: 1,
                        "& strong": { color: "var(--orange)" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Formula (identical to Validation Scoring)
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Step 1 — Per-record impact
                      </Typography>
                      <code>each_records_impact = 1 / total_records</code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 2 — Record-level penalty (per severity)
                      </Typography>
                      <code>
                        record_impact(s) = each_records_impact ×
                        records_with_issues(s) × factor(s) ×
                        impact_of_record_level
                      </code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 3 — Issue-level penalty (per severity)
                      </Typography>
                      <code>
                        issue_impact(s) = each_records_impact × total_issues(s)
                        × factor(s) × impact_of_issue_level
                      </code>
                      <Typography
                        variant="body2"
                        sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                      >
                        Step 4 — Final score
                      </Typography>
                      <code>
                        <strong>
                          score = max(0, 1 − Σ record_impact(s) − Σ
                          issue_impact(s))
                        </strong>
                      </code>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                      >
                        Where <em>s</em> ∈ {"{"} low, medium, high {"}"} and
                        factor(s) is the severity multiplier from the table
                        above. The score is clamped to [0, 1].
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        GRQ-Specific Behavior
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ lineHeight: 1.9 }}
                      >
                        • All research query findings default to{" "}
                        <strong>LOW</strong> severity (medium and high counts
                        are always 0).
                        <br />• The <code>issues_enabled</code> flag (derived
                        from <code>is_issues_data_available</code> on the
                        result) controls whether the issue-level penalty is
                        applied. When <strong>disabled</strong>, all issue
                        impacts are zeroed out.
                        <br />• If a result has <strong>zero records</strong>,
                        the score defaults to <strong>1.0</strong> (no findings
                        = perfect score).
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Worked Example (issues enabled)
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ lineHeight: 1.9 }}
                      >
                        200 result records · all LOW severity · issues enabled
                        <br />
                        <code>each = 1/200 = 0.005</code>
                        <br />
                        <code>
                          record_penalty = 0.005 × 200 × 0.25 × 0.5 = 0.125
                        </code>
                        <br />
                        <code>
                          issue_penalty &nbsp;= 0.005 × 200 × 0.25 × 0.5 = 0.125
                        </code>
                        <br />
                        <code>
                          <strong>
                            score = max(0, 1 − 0.125 − 0.125) = 0.75
                          </strong>
                        </code>
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Worked Example (issues disabled)
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ lineHeight: 1.9 }}
                      >
                        200 result records · all LOW severity · issues{" "}
                        <strong>disabled</strong>
                        <br />
                        <code>each = 1/200 = 0.005</code>
                        <br />
                        <code>
                          record_penalty = 0.005 × 200 × 0.25 × 0.5 = 0.125
                        </code>
                        <br />
                        <code>issue_penalty &nbsp;= 0 (issues disabled)</code>
                        <br />
                        <code>
                          <strong>score = max(0, 1 − 0.125 − 0) = 0.875</strong>
                        </code>
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </TabPanel>
          </>
        )}

        {/* ── Aggregation Score Config ─────────────────────────────────────── */}
        {parentTab === 1 && (
          <>
            <Tabs
              value={aggSubTab}
              onChange={handleAggSubTabChange}
              sx={{
                ...tabIndicatorSx,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Tab label="Validation Run Scoring Defaults" sx={subTabSx} />
              <Tab label="Comparison Run Scoring Defaults" sx={subTabSx} />
              <Tab label="Research Query Run Scoring Defaults" sx={subTabSx} />
              <Tab label="Pipeline Weight Defaults" sx={subTabSx} />
            </Tabs>

            <TabPanel value={aggSubTab} index={0}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Default Validation Run Scoring Parameters
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  These defaults are applied when creating a new
                  per-run/per-resource validation scoring override. They control
                  how a validation result contributes to the aggregated run
                  score.
                </Typography>
                {hasAnyValues(vrd) ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Parameter</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Default Value</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Severity</TableCell>
                          <TableCell align="right">
                            {renderValue(vrd.severity)}
                          </TableCell>
                          <TableCell>
                            Default severity bucket assigned when no override is
                            specified (low / medium / high).
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor</TableCell>
                          <TableCell align="right">
                            {renderValue(vrd.factor)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier applied to the validation
                            contribution in the aggregated run score.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No default configuration found for this category.
                  </Paper>
                )}
                <AggregationFormulaDetails entityLabel="Resources" />
              </Box>
            </TabPanel>

            <TabPanel value={aggSubTab} index={1}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Default Comparison Run Scoring Parameters
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  These defaults are applied when creating a new
                  per-run/per-rule comparison scoring override. They control how
                  a comparison rule's result contributes to the aggregated run
                  score.
                </Typography>
                {hasAnyValues(crd) ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Parameter</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Default Value</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Severity</TableCell>
                          <TableCell align="right">
                            {renderValue(crd.severity)}
                          </TableCell>
                          <TableCell>
                            Default severity bucket assigned when no override is
                            specified (low / medium / high).
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor</TableCell>
                          <TableCell align="right">
                            {renderValue(crd.factor)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier applied to the comparison
                            contribution in the aggregated run score.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No default configuration found for this category.
                  </Paper>
                )}
                <AggregationFormulaDetails entityLabel="Rules" />
              </Box>
            </TabPanel>

            <TabPanel value={aggSubTab} index={2}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Default Research Query Run Scoring Parameters
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  These defaults are applied when creating a new
                  per-run/per-research-query scoring override. They control how
                  a research query's result contributes to the aggregated run
                  score.
                </Typography>
                {hasAnyValues(rqrd) ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Parameter</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Default Value</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Severity</TableCell>
                          <TableCell align="right">
                            {renderValue(rqrd.severity)}
                          </TableCell>
                          <TableCell>
                            Default severity bucket assigned when no override is
                            specified (low / medium / high).
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factor</TableCell>
                          <TableCell align="right">
                            {renderValue(rqrd.factor)}
                          </TableCell>
                          <TableCell>
                            Severity multiplier applied to the research query
                            contribution in the aggregated run score.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No default configuration found for this category.
                  </Paper>
                )}
                <AggregationFormulaDetails entityLabel="Queries" />
              </Box>
            </TabPanel>

            <TabPanel value={aggSubTab} index={3}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Default Pipeline Weight Parameters
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  These defaults control how the three pipeline scores
                  (validation, comparison, research query) are combined into a
                  single aggregated run score. Higher weight = larger
                  contribution to the final score.
                </Typography>
                {hasAnyValues(pwd) ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Parameter</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>Default Value</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Validation Weight</TableCell>
                          <TableCell align="right">
                            {renderValue(pwd.validation_weight)}
                          </TableCell>
                          <TableCell>
                            Weight applied to the validation pipeline score in
                            the aggregated run score.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Comparison Weight</TableCell>
                          <TableCell align="right">
                            {renderValue(pwd.comparison_weight)}
                          </TableCell>
                          <TableCell>
                            Weight applied to the comparison pipeline score in
                            the aggregated run score.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Research Query Weight</TableCell>
                          <TableCell align="right">
                            {renderValue(pwd.research_query_weight)}
                          </TableCell>
                          <TableCell>
                            Weight applied to the research query pipeline score
                            in the aggregated run score.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No default configuration found for this category.
                  </Paper>
                )}
                <AggregationFormulaDetails />
              </Box>
            </TabPanel>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ScoringDefaults;
